import React, { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import XLSX from 'xlsx';
import * as R from 'ramda';
import { COLOR } from '~~elements/Const';
import Button from '~~elements/Button';
import toast from '~~elements/Toast';
import ProgressBar from '~~elements/ProgressBar';
import Loading from '~~elements/Loading';
import { GameResource, FormalGameResource, LeagueResource, SeasonResource } from '~~apis/resource';
import { SectionDiv } from '../styled';
import { init as initExcel, addGame as addGame2Excel, download as downloadExcel } from '../FormalGame/xlsx';
import { useTournamentState } from '../useTournament';
import moment from 'moment';
import ScorebookButton from './ScorebookButton';
import { batchDownloadOpenData, toOpenData } from '../FormalGame/json';

const StyledDiv = styled.div`
  width: 100%;
  .admin-only {
    &--title {
      font-size: 1.1rem;
      margin: 1.5rem 0 0;
      padding: 0.5rem 0 0.2rem;
      border-bottom: 1px solid ${COLOR.secondary};
      display: inline-block;
      &:first-child {
        margin: 0px;
      }
    }
    &--content {
      width: 100%;
      display: block;
      overflow-x: auto;
      padding: 0.5rem 0;
    }
  }

  textarea {
    resize: none;
  }

  table {
    font-size: 0.9rem;
    border-collapse: collapse;
  }
  table,
  th,
  td {
    border: 1px solid #eee;
  }

  td {
    padding: 0.1rem 0.3rem;
  }

  .RE-table {
    text-align: center;
    tr,
    td {
      padding: 0.3rem 0.6rem;
    }
  }

  .trial-seasons {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
  }
`;

const REMapping = {
  B0: '_ _ _',
  B1: '1 _ _',
  B2: '_ 2 _',
  B4: '_ _ 3',
  B3: '1 2 _',
  B5: '1 _ 3',
  B6: '_ 2 3',
  B7: '1 2 3',
};

// 只有 Rebas 管理員才能使用
const FormalAdminOnly = ({ tournament, games }) => {
  const { organizerUniqid, teams } = useTournamentState();

  const [batchDownload, setBatchDownload] = useState(false);
  const [batchTitle, setBatchTitle] = useState('讀取並轉換資料');
  const [batchCompleted, setBatchCompleted] = useState(0);

  const [batchOpenDataDownload, setBatchOpenDataDownload] = useState(false);
  const [batchOpenDataTitle, setBatchOpenDataTitle] = useState('讀取並轉換資料');
  const [batchOpenDataCompleted, setBatchOpenDataCompleted] = useState(0);

  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState({});

  const [externalResourceUpdating, setExternalResourceUpdating] = useState(false);

  const [coefficientUpdating, setCoefficientUpdating] = useState(false);

  const [accumulateUpdating, setAccumulateUpdating] = useState(false);

  const [seasons, setSeasons] = useState([]);
  const [trialCalculating, setTrialCalculating] = useState(false);
  const [trialCalculation, setTrialCalculation] = useState(null);

  useEffect(() => {
    LeagueResource.getLeagueWithSeasons({ leagueUniqid: organizerUniqid })
      .then(({ data }) => {
        setSeasons(data.seasons);
      })
      .catch((e) => {});
  }, [organizerUniqid]);

  const handleBatchBoxScoreClick = () => {
    const startInput = document.querySelector('#excel-start-input').value;
    const endInput = document.querySelector('#excel-end-input').value;

    const startDate = startInput ? moment(startInput) : null;
    const endDate = endInput ? moment(endInput) : null;

    setBatchDownload(true);
    setBatchCompleted(0);

    const finishedGames = R.filter(
      (g) =>
        g.info.status === 'FINISHED' &&
        (!startDate || moment(g.info.started_at).isSameOrAfter(startDate)) &&
        (!endDate || moment(g.info.started_at).isSameOrBefore(endDate)),
      games,
    );
    const amount = finishedGames.length;
    setBatchTitle(`比賽數量： 0 / ${amount}`);

    const sheets = initExcel();

    const writeExcel = () => {
      downloadExcel(`${tournament.title}-逐球數據-${moment().format('YYYYMMDD')}`, sheets);
      setBatchDownload(false);
      toast('處理完成', { status: 'success', second: 5 });
    };

    const getGameInPlayDetail = () => {
      const finishedGame = finishedGames.splice(0, 1)[0];
      GameResource.getGame({ seasonUniqid: tournament.uniqid, gameUniqid: finishedGame.uniqid })
        .then(({ data }) => {
          addGame2Excel(data, sheets);
          setBatchTitle(`剩餘數量： ${finishedGames.length} / ${amount}`);
          setBatchCompleted((((amount - finishedGames.length) * 100) / amount).toFixed(1));
          if (finishedGames.length > 0) {
            getGameInPlayDetail();
            return;
          }
          writeExcel();
        })
        .catch((e) => {
          console.error(e);
          toast(e, { status: 'error' });
        });
    };

    getGameInPlayDetail();
  };

  const handleBatchOpenDataClick = () => {
    const startInput = document.querySelector('#opendata-start-input').value;
    const endInput = document.querySelector('#opendata-end-input').value;

    const startDate = startInput ? moment(startInput) : null;
    const endDate = endInput ? moment(endInput) : null;

    const finishedGames = R.filter(
      (g) =>
        g.info.status === 'FINISHED' &&
        (!startDate || moment(g.info.started_at).isSameOrAfter(startDate)) &&
        (!endDate || moment(g.info.started_at).isSameOrBefore(endDate)),
      games,
    );
    const amount = finishedGames.length;
    if (amount === 0) {
      toast('沒有符合條件的比賽', { status: 'info' });
      return;
    }

    setBatchOpenDataDownload(true);
    setBatchOpenDataCompleted(0);
    setBatchOpenDataTitle(`比賽數量： 0 / ${amount}`);

    const openDataGames = [];
    const writeZip = () => {
      batchDownloadOpenData(`${tournament.title}-OpenData`, openDataGames);
      setBatchOpenDataDownload(false);
      toast('處理完成', { status: 'success', second: 5 });
    };

    const getGameInPlayDetail = () => {
      const finishedGame = finishedGames.splice(0, 1)[0];
      GameResource.getGame({ seasonUniqid: tournament.uniqid, gameUniqid: finishedGame.uniqid })
        .then(({ data }) => {
          const openData = toOpenData(data);
          openDataGames.push({ title: `${data.season.title}-G${data.seq}`, openData });
          setBatchOpenDataTitle(`剩餘數量： ${finishedGames.length} / ${amount}`);
          setBatchOpenDataCompleted((((amount - finishedGames.length) * 100) / amount).toFixed(1));
          if (finishedGames.length > 0) {
            getGameInPlayDetail();
            return;
          }
          writeZip();
        })
        .catch((e) => {
          console.error(e);
          toast(e, { status: 'error' });
        });
    };

    getGameInPlayDetail();
  };

  const handleValidateClick = () => {
    setValidating(true);
    FormalGameResource.validateGamesStatsStatus({ seasonUniqid: tournament.uniqid })
      .then(({ data }) => {
        setValidating(false);
        setValidationResult(data);
      })
      .catch((e) => {
        console.error(e);
        toast(e, { status: 'error' });
        setValidating(false);
      });
  };

  const handleExternalResourceClick = () => {
    setExternalResourceUpdating(true);
    FormalGameResource.updateExternalResource({ leagueUniqid: tournament.league.uniqid })
      .then(({ data }) => {
        setExternalResourceUpdating(false);
        toast('更新成功！', { status: 'success', second: 5 });
      })
      .catch((e) => {
        console.error(e);
        setExternalResourceUpdating(false);
      });
  };

  const handleCoefficientClick = () => {
    setCoefficientUpdating(true);
    FormalGameResource.updateCoefficient({ leagueUniqid: tournament.league.uniqid })
      .then(({ data }) => {
        setCoefficientUpdating(false);
        toast('更新成功！', { status: 'success', second: 5 });
      })
      .catch((e) => {
        console.error(e);
        setCoefficientUpdating(false);
      });
  };

  const handleAccumulateClick = () => {
    setAccumulateUpdating(true);
    toast('請兩分鐘後至前台確認，勿重複點擊！', { status: 'info', second: 5 });
    setTimeout(() => {
      setAccumulateUpdating(false);
    }, 2000);
    FormalGameResource.updateAccumulate({ seasonUniqid: tournament.uniqid }).catch((e) => {
      console.error(e);
      setAccumulateUpdating(false);
    });
  };

  const handleTrialCalculationClick = () => {
    const seasonInputs = document.querySelectorAll('.trial-seasons input');
    const startInput = document.querySelector('#trial-start-input');
    const endInput = document.querySelector('#trial-end-input');
    const teamSelect = document.querySelector('#trial-team-select');

    const seasonUniqids = [];
    for (let seasonInput of seasonInputs) {
      if (!seasonInput.checked) {
        continue;
      }
      seasonUniqids.push(seasonInput.name);
    }
    if (seasonUniqids.length === 0) {
      toast('選擇至少一個賽季', { status: 'error' });
      return;
    }

    const start = startInput.value;
    const end = endInput.value;
    const team = teamSelect.value;
    setTrialCalculating(true);
    FormalGameResource.coefficientTrialCalculation({ leagueUniqid: organizerUniqid, seasonUniqids, start, end, team })
      .then(({ data }) => {
        console.log(data);
        setTrialCalculation(data);
      })
      .catch((e) => {
        toast(e, { status: 'error', second: 5 });
      })
      .finally(() => {
        setTrialCalculating(false);
      });
  };

  const handleWEREDownload = () => {
    const title = 'WE RE 原始係數';
    const wb = XLSX.utils.book_new();
    wb.Props = {
      Title: title,
      Author: 'Rebas 野革',
      CreatedDate: new Date(),
    };

    wb.SheetNames.push('WE 原始係數');
    const weSheet = XLSX.utils.json_to_sheet(trialCalculation.WE);
    wb.Sheets['WE 原始係數'] = weSheet;

    const re = [];
    const reHeader = ['-', '0出', '1出', '2出'];
    const reAppend = (list) => {
      re.push({ [reHeader[0]]: list[0], [reHeader[1]]: list[1], [reHeader[2]]: list[2], [reHeader[3]]: list[3] });
    };
    ['B0', 'B1', 'B2', 'B4', 'B3', 'B5', 'B6', 'B7'].forEach((key) => {
      reAppend([REMapping[key], ...trialCalculation.RE[key].map((sample, _) => sample.runs / sample.times)]);
      reAppend(['有得分', ...trialCalculation.RE[key].map((sample, _) => sample.scored)]);
      reAppend(['總得分', ...trialCalculation.RE[key].map((sample, _) => sample.runs)]);
      reAppend(['次數', ...trialCalculation.RE[key].map((sample, _) => sample.times)]);
    });
    wb.SheetNames.push('RE 原始係數');
    const reSheet = XLSX.utils.json_to_sheet(re);
    wb.Sheets['RE 原始係數'] = reSheet;

    XLSX.writeFile(wb, `${title}.xlsx`);
  };

  const handleDownloadSeasonExcel = () => {
    setBatchDownload(true);
    setBatchCompleted(0);

    // 設定 5 分鐘倒數計時器
    const totalSeconds = 5 * 60; // 5 分鐘 = 300 秒
    let remainingSeconds = totalSeconds;

    const countdownInterval = setInterval(() => {
      remainingSeconds--;
      const progress = Math.max(0, Math.min(90, ((totalSeconds - remainingSeconds) / totalSeconds) * 90));
      setBatchTitle(`等待時間： ${remainingSeconds} 秒`);
      setBatchCompleted(progress.toFixed(1));

      if (remainingSeconds <= 0) {
        clearInterval(countdownInterval);
        setBatchTitle('超時，下載失敗');
      }
    }, 1000);

    SeasonResource.downloadSeasonExcel({ seasonUniqid: tournament.uniqid })
      .then((response) => {
        // ApiService 回傳的是 axios response，需要從 response.data 取得實際資料
        // 但對於檔案下載，我們需要的是 response 本身
        console.log('Response type:', typeof response);
        console.log('Response:', response);

        // 檢查 response 是否有 blob 方法
        if (response && typeof response.blob === 'function') {
          return response.blob();
        } else if (response && response.data) {
          // 如果 response.data 是 blob
          if (response.data instanceof Blob) {
            return response.data;
          }
          // 如果 response.data 是 ArrayBuffer
          if (response.data instanceof ArrayBuffer) {
            return new Blob([response.data]);
          }
          // 如果 response.data 是其他格式，嘗試轉換
          return new Blob([response.data]);
        } else {
          throw new Error('無法處理的回應格式');
        }
      })
      .then((blob) => {
        clearInterval(countdownInterval); // 清除倒數計時器
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${tournament.title}-逐球數據-${moment().format('YYYYMMDD')}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
        setBatchCompleted(100);
        setBatchDownload(false);
        toast('處理完成', { status: 'success', second: 5 });
      })
      .catch((error) => {
        clearInterval(countdownInterval); // 清除倒數計時器
        console.error('Download failed:', error);
        setBatchDownload(false);
        setBatchTitle(`下載失敗：${error.message}`);
        toast('下載失敗', { status: 'error', second: 5 });
      });
  };

  return (
    <SectionDiv>
      <StyledDiv>
        <h4 className="admin-only--title">賽季完成賽事 Excel 下載</h4>
        {/* <p>指定開始日期（不指定就留空）</p>
        <input id="excel-start-input" type="date" />
        <p>指定結束日期（不指定就留空）</p>
        <input id="excel-end-input" type="date" />
        <div className="admin-only--content">
          <i>依照數量決定要等多久。</i>
        </div> */}
        <div className="admin-only--content">
          <Button width="10rem" color="red" disabled={batchDownload} onClick={handleDownloadSeasonExcel}>
            開始下載
          </Button>
        </div>
        <div
          className="admin-only--content"
          style={{ background: 'linear-gradient(242deg, #094bcd, #28cfd7)', padding: '0.5rem' }}
        >
          <ProgressBar isOpen={true} title={batchTitle} completed={batchCompleted} />
        </div>
        <h4 className="admin-only--title">賽季完成賽事 OpenData 下載</h4>
        <p>指定開始日期（不指定就留空）</p>
        <input id="opendata-start-input" type="date" />
        <p>指定結束日期（不指定就留空）</p>
        <input id="opendata-end-input" type="date" />
        <div className="admin-only--content">
          <i>依照數量決定要等多久。</i>
        </div>
        <div className="admin-only--content">
          <Button width="10rem" color="red" disabled={batchOpenDataDownload} onClick={handleBatchOpenDataClick}>
            開始下載
          </Button>
        </div>
        <div
          className="admin-only--content"
          style={{ background: 'linear-gradient(242deg, #094bcd, #28cfd7)', padding: '0.5rem' }}
        >
          <ProgressBar isOpen={true} title={batchOpenDataTitle} completed={batchOpenDataCompleted} />
        </div>
        <h4 className="admin-only--title">重新驗證所有比賽資料完整性</h4>
        <div className="admin-only--content">
          <i>依照數量決定要等多久。</i>
        </div>
        <div className="admin-only--content">
          {validating ? (
            <Loading width="150px" />
          ) : !R.isEmpty(validationResult) ? (
            <Fragment>
              <p>驗證完成，請重新整理後至賽程管理確認</p>
              <p>無誤：{validationResult.NORMAL ?? 0}</p>
              <p>有缺：{validationResult.PITCH_MISSING ?? 0}</p>
              <p>錯誤：{validationResult.CRITICAL ?? 0}</p>
            </Fragment>
          ) : (
            <Button width="10rem" color="red" onClick={handleValidateClick}>
              開始驗證
            </Button>
          )}
        </div>
        <h4 className="admin-only--title">更新近期賽事外部資源（天氣）</h4>
        <div className="admin-only--content">
          <i>依照聯盟賽事總數決定等待時間</i>
        </div>
        <div className="admin-only--content">
          {externalResourceUpdating ? (
            <Loading width="150px" />
          ) : (
            <Button width="10rem" color="red" onClick={handleExternalResourceClick}>
              開始更新
            </Button>
          )}
        </div>
        <h4 className="admin-only--title">更新聯盟係數（目前有 WE、RE 係數可更新）</h4>
        <div className="admin-only--content">
          <i>依照聯盟賽事總數決定等待時間</i>
        </div>
        <div className="admin-only--content">
          {coefficientUpdating ? (
            <Loading width="150px" />
          ) : (
            <Button width="10rem" color="red" onClick={handleCoefficientClick}>
              開始更新
            </Button>
          )}
        </div>
        <h4 className="admin-only--title">重算累計成績（每場 AVG、ERA 等）</h4>
        <div className="admin-only--content">
          <i>依照賽季賽事總數決定等待時間</i>
        </div>
        <div className="admin-only--content">
          {accumulateUpdating ? (
            <Loading width="150px" />
          ) : (
            <Button width="10rem" color="red" onClick={handleAccumulateClick}>
              開始更新
            </Button>
          )}
        </div>
        {seasons.length > 0 && (
          <>
            <h4 className="admin-only--title">聯盟係數試算</h4>
            <div className="admin-only--content">
              <i>不會更新系統資料，只會顯示。會要等一下</i>
            </div>
            {trialCalculation && (
              <div className="admin-only--content">
                <h5>RE</h5>
                <table className="RE-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>0 出</th>
                      <th>1 出</th>
                      <th>2 出</th>
                    </tr>
                  </thead>
                  <tbody>
                    {['B0', 'B1', 'B2', 'B4', 'B3', 'B5', 'B6', 'B7'].map((key) => (
                      <Fragment key={key}>
                        <tr>
                          <td>{REMapping[key]}</td>
                          {trialCalculation.RE[key].map((sample, outs) => (
                            <td key={outs}>{(sample.runs / sample.times).toFixed(3)}</td>
                          ))}
                        </tr>
                        <tr>
                          <td>{`總得分/有得分/次數`}</td>
                          {trialCalculation.RE[key].map((sample, outs) => (
                            <td key={outs}>{`${sample.runs}/${sample.scored}/${sample.times}`}</td>
                          ))}
                        </tr>
                      </Fragment>
                    ))}
                  </tbody>
                </table>
                <h5>WE</h5>
                <Button color="blue" width="10rem" onClick={handleWEREDownload}>
                  下載 WE RE 原始資料
                </Button>
              </div>
            )}
            {trialCalculating && (
              <div className="admin-only--content">
                <Loading width="150px" />
              </div>
            )}
            <div className="admin-only--content" style={{ display: trialCalculating ? 'none' : 'block' }}>
              <p>選擇要作為樣本的賽季（至少一季）</p>
              <div className="trial-seasons">
                {seasons.map((season, i) => (
                  <label key={season.uniqid}>
                    <input name={season.uniqid} type="checkbox" defaultChecked={i === 0} />
                    {season.title}
                  </label>
                ))}
              </div>
              <p>指定開始日期（不指定就留空）</p>
              <input id="trial-start-input" type="date" />
              <p>指定結束日期（不指定就留空）</p>
              <input id="trial-end-input" type="date" />
              <p>指定球隊（不指定就留空）</p>
              <select id="trial-team-select">
                <option value="">不指定</option>
                {teams.map((t) => (
                  <option key={t.origin.uniqid} value={t.origin.uniqid}>
                    {t.name}
                  </option>
                ))}
              </select>
              <p />
              <Button width="10rem" color="blue" onClick={handleTrialCalculationClick}>
                開始試算
              </Button>
            </div>
          </>
        )}
        <ScorebookButton tournament={tournament} games={games} />
      </StyledDiv>
    </SectionDiv>
  );
};

export default FormalAdminOnly;
